import Role from "byzantine/src/Role";
import { ContextForm, useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Account from "byzantine/src/Account";
import {
  useRolesContext,
  useRoleDetail,
} from "byzantine/src/Role/RolesProvider";
import BaseBanner from "../BaseBanner";
import RoleFormBody from "./RoleFormBody";
import useDuplicateRoleNotification from "./useDuplicateRoleNotification";
import useRoleFormData from "./useRoleFormData";
import {
  getDefaultLimits,
  NON_CUSTOM_ROLE_NAMES,
  buildInitialFormData,
  buildRoleFromFormData,
  buildAccountPermissionsPayload,
  buildAccountsOpenedLaterPayload,
  withoutAccountScopedPermissions,
  toRoleFormErrors,
} from "./RoleFormUtils";

interface OrganizationNewRoleProps {
  accounts?: Account[];
  externalAccounts?: Account[];
  orgId: string;
  features?: Record<string, any>;
  isUsingConsumerBillpayJson?: string;
  isDuplicate?: boolean;
  [key: string]: any;
}

interface OrganizationNewRoleFormProps extends OrganizationNewRoleProps {
  duplicateRole: Role | null;
}

type RedirectState =
  | { redirectTo?: { type: "user"; userUuid: string } }
  | undefined;

function OrganizationNewRoleForm({
  accounts = [],
  externalAccounts = [],
  duplicateRole,
  ...props
}: OrganizationNewRoleFormProps) {
  const { createRole } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isUsingConsumerBillpay = !!JSON.parse(
    props.isUsingConsumerBillpayJson || "false"
  );
  const [isCreating, setIsCreating] = useState(false);
  const duplicateRoleNotification = useDuplicateRoleNotification();

  const BaseBannerComponent = BaseBanner as any;

  const defaultLimits = getDefaultLimits(props.features);

  const initialData = buildInitialFormData({
    role: duplicateRole,
    accounts,
    externalAccounts,
    defaultLimits,
    features: props.features,
    isDuplicate: !!props.isDuplicate,
  });

  const { formData, onChange, onAccountPermissionsChange } =
    useRoleFormData(initialData);

  const redirectState = location.state as RedirectState;

  const onSubmit = async (callback: (errors?: unknown) => void) => {
    duplicateRoleNotification.clear();
    try {
      setIsCreating(true);
      const { role: generatedRole } = buildRoleFromFormData(
        formData,
        duplicateRole?.limits
      );

      // Money movement and manage templates permissions are saved per account in
      // `account_permissions`, so they are dropped from the flat `permissions` list.
      const newRole = new Role({
        name: formData.role_name,
        description: formData.role_description,
        permissions: withoutAccountScopedPermissions(generatedRole.permissions),
        limits: generatedRole.limits,
        organization_uuid: props.orgId,
      });

      const createdRole = await createRole({
        ...newRole.serialize(),
        account_permissions: buildAccountPermissionsPayload(formData),
        accounts_opened_later: buildAccountsOpenedLaterPayload(formData),
      });
      sendNotification({
        type: "success",
        text: l10n.getString("org-role-create-success"),
      });
      // Wait for toast to render before redirecting
      setTimeout(() => {
        callback();
        if (redirectState?.redirectTo?.type === "user") {
          navigate(`/${redirectState.redirectTo.userUuid}`);
        } else {
          navigate(`/roles/${createdRole.uuid}`);
        }
      }, 1000);
    } catch (error: unknown) {
      setIsCreating(false);
      if (duplicateRoleNotification.showIfDuplicate(error)) {
        callback();
        return;
      }
      callback(
        toRoleFormErrors(error, {
          nameExists: l10n.getString("org-role-error-name-exists"),
          fallback: l10n.getString("org-role-create-error"),
        })
      );
    }
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <BaseBannerComponent
        bannerName={l10n.getString("org-role-new-title")}
        goBack={() => {
          navigate("/roles");
        }}
      />
      <p>{l10n.getString("org-role-new-description")}</p>
      <ContextForm data={formData} onChange={onChange}>
        <RoleFormBody
          formData={formData}
          onChange={onChange}
          onAccountPermissionsChange={onAccountPermissionsChange}
          onSubmit={onSubmit}
          onCancel={() => navigate("/roles")}
          isSaving={isCreating}
          features={props.features}
          isUsingConsumerBillpay={isUsingConsumerBillpay}
          accounts={accounts}
          externalAccounts={externalAccounts}
          mode="new"
        />
      </ContextForm>
    </div>
  );
}

function OrganizationNewRoleContent(props: OrganizationNewRoleProps) {
  const { roles, isLoading, error } = useRolesContext();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const duplicateRoleInList = props.isDuplicate
    ? roles.find((role: Role) => role.uuid === roleUuid) ?? null
    : null;
  // The roles list is lean, so the role's per-account permissions come from its detail.
  const canDuplicate =
    duplicateRoleInList && !NON_CUSTOM_ROLE_NAMES.has(duplicateRoleInList.name);
  const {
    data: duplicateRoleDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useRoleDetail(canDuplicate ? roleUuid : null);
  const duplicateRole = duplicateRoleDetail ?? null;

  useEffect(() => {
    if (!props.isDuplicate || isLoading || error) {
      return;
    }

    if (!roleUuid) {
      navigate("/roles", { replace: true });
      return;
    }

    if (!duplicateRoleInList) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate("/roles", { replace: true });
      return;
    }

    if (NON_CUSTOM_ROLE_NAMES.has(duplicateRoleInList.name)) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate(`/roles/${duplicateRoleInList.uuid}`, { replace: true });
    }
  }, [
    duplicateRoleInList,
    error,
    isLoading,
    l10n,
    navigate,
    props.isDuplicate,
    roleUuid,
    sendNotification,
  ]);

  const loadError = error || detailError;
  if (props.isDuplicate && loadError) {
    return (
      <div>{`${l10n.getString("org-role-error-toast")}: ${
        (loadError as Error).message
      }`}</div>
    );
  }

  if (
    props.isDuplicate &&
    (isLoading || !canDuplicate || isDetailLoading || !duplicateRole)
  ) {
    return <div>{l10n.getString("org-role-loading")}</div>;
  }

  return <OrganizationNewRoleForm {...props} duplicateRole={duplicateRole} />;
}

export default function OrganizationNewRole(
  props: OrganizationNewRoleProps
): any {
  return <OrganizationNewRoleContent {...props} />;
}
